import { type ChangeEvent, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { usePortalUpgradePreview } from '@/modules/portal/hooks/usePortalUpgradePreview';
import { PortalLoadingState } from '@/modules/portal/components/ui/portal-loading';
import type { PortalBillingPeriod } from '@/modules/portal/types/portalPlans';
import type { PortalSubscriptionPlan } from '@/modules/portal/types/portalPlans';
import {
  formatUpgradePreviewDate,
  formatUpgradePreviewDiscountPercent,
  formatUpgradePreviewMoney,
  formatUpgradePreviewOriginalPrice,
  formatUpgradePreviewRemainingRatio,
  resolveUpgradePreviewErrorMessage,
  resolveUpgradePreviewExpiredLabel,
  resolveUpgradePreviewPricingPolicyKey,
} from '@/modules/portal/utils/portalUpgradePreviewHelpers';
import { Button } from '@/shared/components/ui/button';
import { Icons } from '@/shared/components/ui/icons';
import { cn } from '@/shared/lib/cn';
import {
  startPortalUpgrade,
  uploadPortalUpgradeInstapayProof,
  type PortalUpgradePaymentProvider,
} from '@/modules/portal/services/portalUpgrade.api';

type UpgradePreviewDrawerProps = {
  plan: PortalSubscriptionPlan;
  billingCycle: PortalBillingPeriod;
  onClose: () => void;
};

function DetailRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-0.5 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <span className="shrink-0 text-xs font-medium text-muted-foreground">{label}</span>
      <span className="text-sm text-foreground break-words text-end sm:max-w-[65%]">{value}</span>
    </div>
  );
}

export function UpgradePreviewDrawer({ plan, billingCycle, onClose }: UpgradePreviewDrawerProps) {
  const { t, i18n } = useTranslation();
  const locale = i18n.language.startsWith('ar') ? 'ar-SA' : 'en-US';
  const apiLocale = i18n.language.startsWith('ar') ? 'ar' : 'en';
  const panelRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [paymentProvider, setPaymentProvider] = useState<PortalUpgradePaymentProvider>('instapay');
  const [isSubmittingUpgrade, setIsSubmittingUpgrade] = useState(false);
  const [upgradeError, setUpgradeError] = useState<string | null>(null);
  const [instapayPaymentId, setInstapayPaymentId] = useState<string | null>(null);
  const [proofFile, setProofFile] = useState<File | null>(null);
  const [isUploadingProof, setIsUploadingProof] = useState(false);
  const [proofUploaded, setProofUploaded] = useState(false);

  const { data, isLoading, isError, error, refetch, isFetching } = usePortalUpgradePreview({
    targetPlanCode: plan.plan_code,
    billingCycle,
    enabled: true,
  });

  useEffect(() => {
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  useEffect(() => {
    panelRef.current?.focus();
  }, [plan.id]);

  const errorMessage = isError ? resolveUpgradePreviewErrorMessage(error, t) : null;

  const handleStartUpgrade = async () => {
    if (!data) return;

    setUpgradeError(null);
    setIsSubmittingUpgrade(true);

    try {
      const result = await startPortalUpgrade({
        targetPlanCode: plan.plan_code,
        billingCycle,
        paymentProvider,
        locale: apiLocale,
        idempotencyKey: crypto.randomUUID(),
      });

      if (result.payment_provider === 'myfatoorah') {
        window.location.href = result.payment_url;
        return;
      }

      setInstapayPaymentId(result.payment_id);
    } catch (err) {
      setUpgradeError(err instanceof Error ? err.message : 'تعذر بدء الترقية. حاول مرة أخرى.');
    } finally {
      setIsSubmittingUpgrade(false);
    }
  };

  const handleProofFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0] ?? null;
    setProofFile(file);
    setUpgradeError(null);
  };

  const handleUploadProof = async () => {
    if (!instapayPaymentId || !proofFile) return;

    setUpgradeError(null);
    setIsUploadingProof(true);

    try {
      await uploadPortalUpgradeInstapayProof(instapayPaymentId, proofFile);
      setProofUploaded(true);
    } catch (err) {
      setUpgradeError(err instanceof Error ? err.message : 'تعذر رفع إيصال الترقية.');
    } finally {
      setIsUploadingProof(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex" role="presentation">
      <button
        type="button"
        className="absolute inset-0 bg-black/40 backdrop-blur-[1px]"
        aria-label={t('common.close')}
        onClick={onClose}
      />
      <div
        ref={panelRef}
        tabIndex={-1}
        role="dialog"
        aria-modal="true"
        aria-labelledby="upgrade-preview-title"
        className={cn(
          'relative ms-auto flex h-full w-full max-w-lg flex-col border-s border-border bg-background shadow-xl',
          'animate-in slide-in-from-end duration-200',
        )}
      >
        <header className="flex items-start justify-between gap-3 border-b border-border px-4 py-4 sm:px-5">
          <div className="min-w-0 space-y-1">
            <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
              {t('portal.upgradePreview.eyebrow')}
            </p>
            <h2
              id="upgrade-preview-title"
              className="truncate text-lg font-semibold text-foreground"
            >
              {t('portal.upgradePreview.title')}
            </h2>
            <p className="text-sm text-muted-foreground">{t('portal.upgradePreview.subtitle')}</p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="shrink-0 rounded-xl"
            aria-label={t('common.close')}
            onClick={onClose}
          >
            <Icons.X className="h-4 w-4" />
          </Button>
        </header>

        <div className="flex-1 overflow-y-auto px-4 py-4 sm:px-5">
          {isLoading ? (
            <PortalLoadingState
              size="md"
              message={t('portal.upgradePreview.loading')}
              className="min-h-[240px] justify-center"
            />
          ) : isError ? (
            <div className="portal-glass-panel space-y-4 rounded-2xl border-destructive/30 p-5">
              <div className="flex items-start gap-3">
                <Icons.XCircle className="mt-0.5 h-5 w-5 shrink-0 text-destructive" aria-hidden />
                <div className="space-y-1">
                  <h3 className="text-base font-semibold text-foreground">
                    {t('portal.upgradePreview.error.title')}
                  </h3>
                  <p className="text-sm text-muted-foreground">{errorMessage}</p>
                </div>
              </div>
              <Button
                type="button"
                variant="outline"
                className="rounded-xl"
                disabled={isFetching}
                onClick={() => void refetch()}
              >
                {t('common.retry')}
              </Button>
            </div>
          ) : data ? (
            <div className="space-y-6">
              <section className="portal-glass-panel space-y-3 rounded-2xl border-border/60 p-4">
                <h3 className="text-sm font-semibold text-foreground">
                  {t('portal.upgradePreview.sections.plans')}
                </h3>
                <DetailRow
                  label={t('portal.upgradePreview.fields.currentPlan')}
                  value={data.current_plan.name}
                />
                <DetailRow
                  label={t('portal.upgradePreview.fields.targetPlan')}
                  value={data.target_plan.name}
                />
                <DetailRow
                  label={t('portal.upgradePreview.fields.billingCycle')}
                  value={t(`portal.plans.billingToggle.${data.billing_cycle}`)}
                />
                <DetailRow
                  label={t('portal.upgradePreview.fields.currency')}
                  value={data.currency}
                />
              </section>

              <section className="portal-glass-panel space-y-3 rounded-2xl border-border/60 p-4">
                <h3 className="text-sm font-semibold text-foreground">
                  {t('portal.upgradePreview.sections.pricing')}
                </h3>
                <DetailRow
                  label={t('portal.upgradePreview.fields.currentPrice')}
                  value={formatUpgradePreviewMoney(
                    data.current_plan.price_amount,
                    data.currency,
                    locale,
                  )}
                />
                <DetailRow
                  label={t('portal.upgradePreview.fields.targetPrice')}
                  value={formatUpgradePreviewMoney(
                    data.target_plan.price_amount,
                    data.currency,
                    locale,
                  )}
                />
                <DetailRow
                  label={t('portal.upgradePreview.fields.originalPrice')}
                  value={formatUpgradePreviewOriginalPrice(
                    data.target_plan.original_price_amount,
                    data.currency,
                    locale,
                  )}
                />
                <DetailRow
                  label={t('portal.upgradePreview.fields.discountPercent')}
                  value={formatUpgradePreviewDiscountPercent(
                    data.target_plan.discount_percent,
                    locale,
                  )}
                />
                <DetailRow
                  label={t('portal.upgradePreview.fields.currentRemainingValue')}
                  value={formatUpgradePreviewMoney(
                    data.calculation.current_remaining_value,
                    data.currency,
                    locale,
                  )}
                />
                <DetailRow
                  label={t('portal.upgradePreview.fields.targetRemainingValue')}
                  value={formatUpgradePreviewMoney(
                    data.calculation.target_remaining_value,
                    data.currency,
                    locale,
                  )}
                />
                <DetailRow
                  label={t('portal.upgradePreview.fields.amountDue')}
                  value={formatUpgradePreviewMoney(
                    data.calculation.amount_due,
                    data.currency,
                    locale,
                  )}
                />
                <DetailRow
                  label={t('portal.upgradePreview.fields.pricingPolicy')}
                  value={t(resolveUpgradePreviewPricingPolicyKey(data.calculation.pricing_policy))}
                />
              </section>

              <section className="portal-glass-panel space-y-3 rounded-2xl border-border/60 p-4">
                <h3 className="text-sm font-semibold text-foreground">
                  {t('portal.upgradePreview.sections.subscriptionPeriod')}
                </h3>
                <DetailRow
                  label={t('portal.upgradePreview.fields.periodStart')}
                  value={formatUpgradePreviewDate(data.subscription_period.period_start, locale)}
                />
                <DetailRow
                  label={t('portal.upgradePreview.fields.periodEnd')}
                  value={formatUpgradePreviewDate(data.subscription_period.period_end, locale)}
                />
                <DetailRow
                  label={t('portal.upgradePreview.fields.periodStatus')}
                  value={resolveUpgradePreviewExpiredLabel(data.subscription_period.is_expired, t)}
                />
                <DetailRow
                  label={t('portal.upgradePreview.fields.remainingRatio')}
                  value={formatUpgradePreviewRemainingRatio(
                    data.subscription_period.remaining_ratio,
                    locale,
                  )}
                />
              </section>

              {data.notes.length > 0 ? (
                <section className="space-y-2">
                  <h3 className="text-sm font-semibold text-foreground">
                    {t('portal.upgradePreview.sections.notes')}
                  </h3>
                  <ul className="space-y-2 text-sm text-muted-foreground">
                    <li className="flex items-start gap-2">
                      <Icons.CheckCircle
                        className="mt-0.5 h-4 w-4 shrink-0 text-primary"
                        aria-hidden
                      />
                      <span>{t('portal.upgradePreview.localizedNotes.paymentNotCreated')}</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <Icons.CheckCircle
                        className="mt-0.5 h-4 w-4 shrink-0 text-primary"
                        aria-hidden
                      />
                      <span>
                        {t('portal.upgradePreview.localizedNotes.subscriptionNotChanged')}
                      </span>
                    </li>
                  </ul>
                </section>
              ) : null}

              <p className="text-center text-xs text-muted-foreground">
                {t('portal.upgradePreview.safeNote')}
              </p>
            </div>
          ) : null}
        </div>

        <footer className="space-y-3 border-t border-border px-4 py-4 sm:px-5">
          {data && !instapayPaymentId ? (
            <div className="space-y-3">
              <p className="text-sm font-semibold text-foreground">طريقة الدفع</p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                <label
                  className={cn(
                    'flex cursor-pointer items-center justify-between rounded-xl border p-3 text-sm',
                    paymentProvider === 'myfatoorah'
                      ? 'border-primary bg-primary/5'
                      : 'border-border',
                  )}
                >
                  <span>ماي فاتورة</span>
                  <input
                    type="radio"
                    name="upgrade-payment-provider"
                    value="myfatoorah"
                    checked={paymentProvider === 'myfatoorah'}
                    onChange={() => setPaymentProvider('myfatoorah')}
                  />
                </label>
                <label
                  className={cn(
                    'flex cursor-pointer items-center justify-between rounded-xl border p-3 text-sm',
                    paymentProvider === 'instapay'
                      ? 'border-primary bg-primary/5'
                      : 'border-border',
                  )}
                >
                  <span>InstaPay</span>
                  <input
                    type="radio"
                    name="upgrade-payment-provider"
                    value="instapay"
                    checked={paymentProvider === 'instapay'}
                    onChange={() => setPaymentProvider('instapay')}
                  />
                </label>
              </div>

              {upgradeError ? (
                <p className="text-sm text-destructive" role="alert">
                  {upgradeError}
                </p>
              ) : null}

              <Button
                type="button"
                className="w-full rounded-xl"
                disabled={isSubmittingUpgrade}
                onClick={handleStartUpgrade}
              >
                {isSubmittingUpgrade ? 'جاري بدء الترقية…' : 'تأكيد الترقية'}
              </Button>
            </div>
          ) : null}

          {instapayPaymentId ? (
            <div className="space-y-3 rounded-2xl border border-border/60 p-3">
              <p className="text-sm font-semibold text-foreground">بيانات تحويل InstaPay</p>

              <div className="space-y-2 rounded-2xl border border-border/60 bg-muted/20 p-3">
                <div className="flex items-center justify-between gap-3 rounded-xl bg-background/80 p-3">
                  <div className="text-end">
                    <p className="text-xs text-muted-foreground">اسم المستفيد</p>
                    <p className="font-bold text-foreground">Wared Technology</p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-xl"
                    onClick={() => void navigator.clipboard?.writeText('Wared Technology')}
                  >
                    نسخ
                  </Button>
                </div>

                <div className="flex items-center justify-between gap-3 rounded-xl bg-background/80 p-3">
                  <div className="text-end">
                    <p className="text-xs text-muted-foreground">رقم التحويل / InstaPay</p>
                    <p className="font-bold text-foreground ltr" dir="ltr">
                      01031846418
                    </p>
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    className="rounded-xl"
                    onClick={() => void navigator.clipboard?.writeText('01031846418')}
                  >
                    نسخ
                  </Button>
                </div>
              </div>

              <p className="text-sm font-semibold text-foreground">رفع إيصال InstaPay للترقية</p>
              <p className="text-xs text-muted-foreground">
                حوّل مبلغ الترقية إلى الرقم الموضح، ثم ارفع صورة الإيصال ليتم مراجعتها من المشرف.
              </p>

              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="sr-only"
                onChange={handleProofFileChange}
              />

              <div className="flex flex-wrap items-center gap-2">
                <Button
                  type="button"
                  variant="outline"
                  className="rounded-xl"
                  disabled={isUploadingProof || proofUploaded}
                  onClick={() => fileInputRef.current?.click()}
                >
                  <Icons.Upload className="me-2 h-4 w-4" />
                  اختيار صورة الإيصال
                </Button>

                {proofFile ? (
                  <span className="max-w-[220px] truncate text-xs text-muted-foreground">
                    {proofFile.name}
                  </span>
                ) : null}
              </div>

              {upgradeError ? (
                <p className="text-sm text-destructive" role="alert">
                  {upgradeError}
                </p>
              ) : null}

              {proofUploaded ? (
                <p className="rounded-xl bg-primary/10 p-3 text-sm font-medium text-primary">
                  تم رفع الإيصال بنجاح. سيتم مراجعة طلب الترقية من المشرف.
                </p>
              ) : (
                <Button
                  type="button"
                  className="w-full rounded-xl"
                  disabled={!proofFile || isUploadingProof}
                  onClick={handleUploadProof}
                >
                  {isUploadingProof ? 'جاري رفع الإيصال…' : 'إرسال الإيصال للمراجعة'}
                </Button>
              )}
            </div>
          ) : null}

          <Button type="button" variant="outline" className="w-full rounded-xl" onClick={onClose}>
            إغلاق
          </Button>
        </footer>
      </div>
    </div>
  );
}
